import React, { useState } from "react";
import {
  Button,
  IconButton,
  Card,
  CardActions,
  CardContent,
  Collapse,
  CardHeader,
  Grow,
  CircularProgress,
  LinearProgress
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import SaveIcon from "@material-ui/icons/Save";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import DeleteForeverOutlinedIcon from "@material-ui/icons/DeleteForeverOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { useForm } from 'react-hook-form';
import { useDispatch } from "react-redux";
import actionsTopButtonStyles from "../../../../../../components/App/customMaterialStyles";
import { showConfirmDialog } from "../../../../../../../redux/actions/uiConfirmDialog";
import AssignmentService from "../../../../../../../services/AssignmentService";
import { showSnackBar } from "../../../../../../../redux/actions/ui";
import { datetimeToString } from "../../../../../../../utils/datetime";
import AssignmentForm from "./AssignmentForm";


const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: "1em"
  },
  collapse: {
    borderTop: "1px solid"
  }
}));

function AssignmentItem(props) {
  const { loading = false } = props;  
  const dispatch = useDispatch();
  
  const { id, name, modified_datetime } = props.data || {};
  const { courseId, lessonId } = props;

  const classes = useStyles();
  const actionsTopButtonStylesClasses = actionsTopButtonStyles();

  const [expanded, setExpanded] = useState(false);
  const [isEditableForm, setIsEditableForm] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  const { register, errors, handleSubmit, control } = useForm({
    validateCriteriaMode: "all",
    mode: "onChange"
  });
  
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  
  const handleClickOpenDialogRemove = () => {
    dispatch(
      showConfirmDialog(
        "Remove this Assignment ?", 
        "You won't be able to revert this!", 
        handleOnDelete
      )
    );
  };

  const handleOnDelete = async () => {    
    try{
      const assignmentId = id;
      await AssignmentService.deleteAssignment(courseId, lessonId, id);
      props.removeItem(assignmentId);
    }catch (error) {
      setIsLoadingSubmit(false);
    }  
  }
  
  const editAssignment = async data => {    
    const dataToUpdate = {...data, id};
    setIsLoadingSubmit(true);
    try{      
      dataToUpdate.start_date = dataToUpdate.end_date;
      await AssignmentService.updateAssignment(courseId, lessonId, dataToUpdate);
      setIsLoadingSubmit(false);      
      dispatch(showSnackBar("Successfully edited!"));
      if(name !== dataToUpdate.name){
        // if the name changed we need to update the list
        props.updateAssignmentsFromList();
      }
    }catch (error) {
      setIsLoadingSubmit(false);
      dispatch(showSnackBar("An error occurred!", 'error'));
    }  
  };

  return (
    <Card className={classes.root}>
      {/* header */}
      <CardHeader
        action={
          <React.Fragment >
            {expanded && (
              <React.Fragment>
                <Grow in={expanded} {...(expanded ? { timeout: 1000 } : {})}>
                  <IconButton 
                    aria-label="edit-assignment" 
                    className={clsx(actionsTopButtonStylesClasses.actionsTopButtons, {
                      [actionsTopButtonStylesClasses.actionsTopButtonsSelected]: isEditableForm
                    }) } 
                    onClick={() => setIsEditableForm(!isEditableForm) }>
                    <EditOutlinedIcon />
                  </IconButton>
                </Grow>    
                <Grow in={expanded} {...(expanded ? { timeout: 500 } : {})} >
                  <IconButton aria-label="delete-assignment" className={actionsTopButtonStylesClasses.actionsTopButtons} onClick={handleClickOpenDialogRemove}>
                    <DeleteForeverOutlinedIcon />
                  </IconButton>
                </Grow>
              </React.Fragment>
            )}

            {loading ?
              <Skeleton
                variant="circle"
                width={50}
                height={50}
                style={{ marginBottom: 0 }}
              />
              :
              <IconButton
                className={clsx(actionsTopButtonStylesClasses.expand, {
                  [actionsTopButtonStylesClasses.expandOpen]: expanded
                }, actionsTopButtonStylesClasses.actionsTopButtons)}
                onClick={handleExpandClick}
                aria-expanded={expanded}
                aria-label="Expand"
                data-testid="expand-assignment"
              >
                <ExpandMoreIcon />
              </IconButton>              
            }
          </React.Fragment>
        }
        title={
          loading ? (
            <Skeleton
              variant="rect"
              width={180}
              height={20}
              style={{ marginBottom: 6 }}
            />
          ) : (
            name
          )
        }
        subheader={
          loading ? (
            <Skeleton variant="rect" width={210} height={10} />            
          ) : (
            `Last edit: ${datetimeToString(modified_datetime, "MM-DD-YYYY HH:mm")}`
          )
        }
      />
      
      {/* Loading */}
      {isLoadingSubmit && <LinearProgress />}      

      {/* Body forms */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent className={classes.collapse}>
          <AssignmentForm isEditableForm={isEditableForm} data={props.data} formConfig={ {register, errors, handleSubmit, control} }>
            {/* Action Buttons to save */}
            {isEditableForm && (
              <CardActions className={actionsTopButtonStylesClasses.saveAction} >
                <Button
                  variant="outlined"
                  type="submit"
                  disabled={isLoadingSubmit} 
                  onClick={() => setIsEditableForm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  color="primary"     
                  type="submit"            
                  onClick={handleSubmit(editAssignment)}
                  disabled={isLoadingSubmit} 
                  startIcon={
                    isLoadingSubmit ? <CircularProgress size={24}/> : <SaveIcon />
                  }>
                  Save
                </Button>
              </CardActions>
            )} 
          </AssignmentForm>
        </CardContent>
      </Collapse>
      
    </Card>
  );
}

export default AssignmentItem;
